import { PropsWithChildren } from 'react';
import styled from 'styled-components';

import { breakpoint } from 'app/theme/theme';
import { fileUploader } from 'app/translations/dictionary';

interface DropzoneSubmitAlertProps {
  onAccept: () => void;
}

interface DropzoneCloseAlertProps extends DropzoneSubmitAlertProps {
  onReject: () => void;
}

interface DropzoneFileTypesAlertProps extends DropzoneSubmitAlertProps {
  invalidExtensions: string[];
}

const DropzoneAlertContainer = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  z-index: ${({ theme }) => theme.zIndex.popup};
  background-color: ${({ theme }) => theme.palette.secondary};
`;

const Header = styled.header`
  text-align: center;
  font-weight: 600;
  margin-bottom: 1rem;

  ${breakpoint.up.sm`
    font-size: 1.8rem;
  `}
`;

const Content = styled.p`
  font-size: 1.4rem;
  text-align: center;
  padding: 0 4rem;

  ${breakpoint.up.xs`
    padding: 0 7.6rem;
  `}

  ${breakpoint.up.sm`
    font-size: 1.8rem;
  `}

  ${breakpoint.up.md`
    padding: 0 6rem;  
  `}
`;

const ButtonsContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const ActionButton = styled.button`
  padding: 0;
  margin: 0;
  box-shadow: none;
  background-color: transparent;
  border: none;
  cursor: pointer;
  font-size: 1.8rem;
  transition: 0.3s all;
  font-weight: 600;

  &:hover {
    color: ${({ theme }) => theme.palette.white};
    text-decoration: underline;
  }

  &:focus {
    outline: none;
  }

  :not(:last-of-type) {
    margin-right: 3rem;
  }
`;

const Title = styled.div`
  font-weight: 600;
  color: ${({ theme }) => theme.palette.primary};
  width: 100%;
  font-size: 1.8rem;
  text-align: center;
  margin-bottom: 1.5rem;
`;

const Alert = ({
  header,
  content,
  children,
  title = '',
}: PropsWithChildren<{ header: string; content: string; title?: string }>) => (
  <DropzoneAlertContainer>
    {title && <Title>{title}</Title>}
    <Header>{header}</Header>
    <Content>{content}</Content>
    <ButtonsContainer>{children}</ButtonsContainer>
  </DropzoneAlertContainer>
);

export const DropzoneSubmitAlert = ({ onAccept }: DropzoneSubmitAlertProps): JSX.Element => (
  <Alert {...fileUploader.submitAlert}>
    <ActionButton onClick={onAccept}>{fileUploader.submitAlert.accept}</ActionButton>
  </Alert>
);

export const DropzoneInvalidFileTypeAlert = ({
  onAccept,
  invalidExtensions,
}: DropzoneFileTypesAlertProps): JSX.Element => (
  <Alert
    {...fileUploader.fileTypeAlert}
    content={`${fileUploader.fileTypeAlert.content} ${invalidExtensions.map((ext) => `.${ext}`).join(', ')}`}
  >
    <ActionButton onClick={onAccept}>{fileUploader.submitAlert.accept}</ActionButton>
  </Alert>
);

export const DropzoneTooBigFilesAlert = ({ onAccept }: DropzoneSubmitAlertProps) => (
  <Alert {...fileUploader.tooBigFilesAlert}>
    <ActionButton onClick={onAccept}>{fileUploader.tooBigFilesAlert.accept}</ActionButton>
  </Alert>
);

export const DropzoneCloseAlert = ({ onAccept, onReject }: DropzoneCloseAlertProps): JSX.Element => (
  <Alert {...fileUploader.closeAlert}>
    <ActionButton onClick={onAccept}>{fileUploader.closeAlert.accept}</ActionButton>
    <ActionButton onClick={onReject}>{fileUploader.closeAlert.reject}</ActionButton>
  </Alert>
);
